import { Package, Truck, Globe, Clock } from 'lucide-react';
import { SEOHead } from '@/components/seo';
import { PAGE_SEO } from '@/lib/seo-config';

export default function Shipping() {
  return (
    <div className="bg-[#050505] min-h-screen pt-24">
      <SEOHead
        title={PAGE_SEO.shipping.title}
        description={PAGE_SEO.shipping.description}
        keywords={PAGE_SEO.shipping.keywords}
        url="/legal/shipping"
        breadcrumbs={[
          { name: 'Home', url: '/' },
          { name: 'Legal', url: '/legal/terms' },
          { name: 'Shipping & Delivery', url: '/legal/shipping' },
        ]}
      />
      <section className="py-16 px-4">
        <div className="max-w-3xl mx-auto">
          <h1 data-testid="shipping-title" className="text-4xl font-bold text-white mb-4 section-marker font-mono">Shipping & Delivery Policy</h1>
          <p className="text-xs font-mono text-zinc-500 mb-12">Last updated: February 1, 2026</p>

          {/* Digital Service Notice */}
          <div className="bg-emerald-500/5 border border-emerald-500/20 rounded-sm p-6 mb-12">
            <div className="flex items-start gap-4">
              <Package size={24} className="text-emerald-400 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="text-white font-mono font-bold mb-2">Digital Service Delivery</h3>
                <p className="text-sm text-zinc-400 leading-relaxed">
                  ClawStinger is a digital service platform. All products and services are delivered electronically. 
                  There are no physical goods to ship. Your instances and services are provisioned instantly upon purchase.
                </p>
              </div>
            </div>
          </div>

          {/* Delivery Info Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-12">
            {[
              { icon: Clock, title: 'Instance Provisioning', desc: 'Under 60 seconds', color: 'text-emerald-400' },
              { icon: Globe, title: 'Global Availability', desc: '8 regions worldwide', color: 'text-cyan-400' },
              { icon: Truck, title: 'Delivery Method', desc: '100% digital', color: 'text-emerald-400' },
            ].map((item, i) => {
              const Icon = item.icon;
              return (
                <div key={i} className="bg-[#0A0A0A] border border-zinc-800 rounded-sm p-5 text-center">
                  <Icon size={24} className={`${item.color} mx-auto mb-3`} />
                  <h3 className="text-sm font-mono text-white font-bold">{item.title}</h3>
                  <p className="text-xs text-zinc-500 mt-1">{item.desc}</p>
                </div>
              );
            })}
          </div>

          <div className="space-y-8">
            {[
              { 
                title: '1. Service Delivery', 
                body: 'ClawStinger provides managed cloud hosting services delivered entirely online. Upon completing your purchase, services are activated immediately. Instance provisioning typically completes within 60 seconds. You will receive email confirmation with access details.' 
              },
              { 
                title: '2. Instant Access', 
                body: 'Once payment is confirmed, you gain immediate access to your dashboard and can begin creating instances. No waiting period or manual approval is required for standard plans. Enterprise plans may require additional verification.' 
              },
              { 
                title: '3. Regional Deployment', 
                body: 'Instances can be deployed to any of our 8 global regions: US-East, US-West, EU-West, EU-Central, Asia-Pacific (Singapore), Asia-Pacific (Tokyo), South America, and Australia. Region selection is available during instance creation.' 
              },
              { 
                title: '4. Service Activation', 
                body: 'Subscription plans are activated upon successful payment. Credits are added to your account balance instantly. All features included in your plan become available immediately without delay.' 
              },
              { 
                title: '5. Access Credentials', 
                body: 'RDP/SSH access credentials for your instances are generated automatically and displayed in your dashboard. Email notifications with quick-access links are sent upon instance creation. Credentials can be regenerated at any time.' 
              },
              { 
                title: '6. Physical Merchandise (If Applicable)', 
                body: 'On rare occasions, we may offer promotional physical merchandise (stickers, t-shirts, etc.). If applicable, these items ship within 7-14 business days via standard mail. Shipping costs and delivery times vary by location. Physical merchandise availability is limited to select regions.' 
              },
              { 
                title: '7. Delivery Failures', 
                body: 'If you do not receive access to your services within 1 hour of payment, please check your spam folder for confirmation emails. If issues persist, contact support@clawstinger.com with your order confirmation.' 
              },
              { 
                title: '8. Service Availability', 
                body: 'Our services are available 24/7/365 with a 99.9% uptime SLA. Scheduled maintenance windows are announced 48 hours in advance and typically occur during low-traffic periods (2-4 AM UTC).' 
              },
              { 
                title: '9. Contact Us', 
                body: 'For any delivery or access issues, contact support@clawstinger.com. Our support team is available Monday-Friday, 9 AM - 6 PM UTC, with emergency support available 24/7 for critical issues.' 
              },
            ].map((s, i) => (
              <div key={i}>
                <h2 className="text-lg font-mono text-white font-bold mb-2">{s.title}</h2>
                <p className="text-sm text-zinc-400 leading-relaxed">{s.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
